import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { of, Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
 
import { environment } from '../../../environments/environment';
import { InMemoryStorageService } from '../../services/in-memory-storage.service';
import { shouldSetCustomerCreditBlockOnDraftSave } from '../../item-details/item-utils';
import { getAuthHttpHeaders, getAuthHttpOptions } from '../../utils/auth-request.util';
 
@Injectable({
    providedIn: 'root'
})
export class ProductService {
    private baseUrl = environment.apiUrl;
    public company: string = '';
    public division: string = '';
    constructor(
        private http: HttpClient,
        private inMemoryStorageService: InMemoryStorageService
    ) {
        this.isSyncing = localStorage.getItem('isSyncing') === 'true';
    }
 
    setCompanyAndDivision(companyId: string, divisionId: string) {
        this.company = companyId;
        this.division = divisionId;
        // Optionally, persist to localStorage for reloads
        this.inMemoryStorageService.set('companyId', companyId);
        this.inMemoryStorageService.set('divisionId', divisionId);
    }
 
    getCompanyId() {
        // Try to get from memory, fallback to localStorage
        return this.company || this.inMemoryStorageService.get('companyId') || '';
    }
 
    getDivisionId() {
        return this.division || this.inMemoryStorageService.get('divisionId') || '';
    }

    getWarehouseId(): string {
        return String(this.inMemoryStorageService.get('warehouseId') ?? '').trim();
    }

    /**
     * Orders1 warehouse filter: only when division + current WH are in env scoped lists.
     * Div 100 WH 130 → returns "130"; WH 101/110 → undefined (division-wide).
     */
    getOrderListWarehouseParam(): string | undefined {
        const division = this.getDivisionId();
        const warehouse = this.getWarehouseId();
        if (!division || !warehouse) {
            return undefined;
        }
        const allowedDivisions = String(environment.orderListWarehouseFilterDivisions ?? '')
            .split(',')
            .map((d) => d.trim())
            .filter(Boolean);
        if (!allowedDivisions.includes(division)) {
            return undefined;
        }
        const scopedKey = `orderListWarehouseScopedWhs_${division}` as keyof typeof environment;
        const scopedRaw = environment[scopedKey];
        const scoped = String(scopedRaw ?? '')
            .split(',')
            .map((w) => w.trim())
            .filter(Boolean);
        return scoped.includes(warehouse) ? warehouse : undefined;
    }

    private appendOrderListWarehouseParam(params: Record<string, string | number>): Record<string, string | number> {
        const warehouse = this.getOrderListWarehouseParam();
        if (warehouse) {
            params['warehouse'] = warehouse;
        }
        return params;
    }

    searchCustomers(query: string): Observable<any[]> {
        const company = this.getCompanyId();
        const division = this.getDivisionId();
        console.log('Default Order Type:', environment.defualtOrderType);
        console.log('Default Order Types Germany:', environment.defaultOrderTypesGermany);
        console.log('Default Order Types For France:', environment.defaultOrderTypesForFrance);
 
        let orderTypeQuery = '';
 
        if (division === '400') {
            // For division 400 → single default order type
            orderTypeQuery = `ORTP:'${encodeURIComponent(environment.defualtOrderType)}'`;
        } else if (division === '100') {
            // For Germany → multiple order types from array
            const germanyTypes = environment.defaultOrderTypesGermany.map((type) => `ORTP:${encodeURIComponent(type)}`).join(' OR ');
 
            orderTypeQuery = `(${germanyTypes})`;
        } else if (division === '300') {
            // For France → multiple order types from array
            const franceTypes = environment.defaultOrderTypesForFrance.map((type) => `ORTP:${encodeURIComponent(type)}`).join(' OR ');
            orderTypeQuery = `(${franceTypes})`;
        }else{
            orderTypeQuery = `ORTP:'${encodeURIComponent(environment.defualtOrderType)}'`;
        }
 
        const url = `${environment.apiUrl}m3/SearchCustomer/Search?SQRY=CUNM:${encodeURIComponent(query + '*')} STAT:${encodeURIComponent('20')} ${orderTypeQuery}&company=${encodeURIComponent(company)}&division=${encodeURIComponent(division)}`;
 
        return this.http.get<any[]>(url);
    }
     searchCustomerNumbers(query: string): Observable<any[]> {
        const company = this.getCompanyId();
        const division = this.getDivisionId();
        console.log('Default Order Type:', environment.defualtOrderType);
        console.log('Default Order Types Germany:', environment.defaultOrderTypesGermany);
        console.log('Default Order Types For France:', environment.defaultOrderTypesForFrance);
 
        let orderTypeQuery = '';
 
        if (division === '400') {
            // For division 400 → single default order type
            orderTypeQuery = `ORTP:'${encodeURIComponent(environment.defualtOrderType)}'`;
        } else if (division === '100') {
            // For Germany → multiple order types from array
            const germanyTypes = environment.defaultOrderTypesGermany.map((type) => `ORTP:${encodeURIComponent(type)}`).join(' OR ');
 
            orderTypeQuery = `(${germanyTypes})`;
        } else if (division === '300') {
            // For France → multiple order types from array
            const franceTypes = environment.defaultOrderTypesForFrance.map((type) => `ORTP:${encodeURIComponent(type)}`).join(' OR ');
            orderTypeQuery = `(${franceTypes})`;
        }else{
            orderTypeQuery = `ORTP:'${encodeURIComponent(environment.defualtOrderType)}'`;
        }
 
        const url = `${environment.apiUrl}m3/SearchCustomer/Search?SQRY=CUNO:${encodeURIComponent(query + '*')} STAT:${encodeURIComponent('20')} ${orderTypeQuery}&company=${encodeURIComponent(company)}&division=${encodeURIComponent(division)}`;
 
        return this.http.get<any[]>(url);
    }
 
    sendSelectedCustomer(customer: any): Observable<any> {
        const url = `${environment.apiUrl}m3/SelectedCustomer`;
        return this.http.post<any>(url, customer, {
            params: {
                company: this.getCompanyId(),
                division: this.getDivisionId()
            }
        });
    }
 
    getCustomerDetails(customerId: string, facilityId: string): Observable<any> {
        const url = `${environment.apiUrl}m3/SearchCustomer/Details`;
        return this.http.get<any>(url, {
            ...getAuthHttpOptions(),
            params: {
                customerId,
                company: this.getCompanyId(),
                division: this.getDivisionId(),
                faci: facilityId
            }
        });
    }
    getFacilities(companyId: string): Observable<any[]> {
        const url = `${environment.apiUrl}facilities/LstFacility?company=${companyId}`;
        return this.http.get<any[]>(url);
    }
    getWarehouses(companyId: string): Observable<any[]> {
        const url = `${environment.apiUrl}facilities/LstWarehouses?company=${companyId}`;
        return this.http.get<any[]>(url);
    }
    getOrderTypes(companyId: string): Observable<any> {
        const url = `${environment.apiUrl}m3/SearchCustomer/order-type?company=${companyId}`;
        return this.http.get<any>(url);
    }
    // ✅ Export order headers only
    exportOrderHeaders(
        company: string,
        division: string,
        fromDate: string,
        toDate: string,
        dateType: 'orderCreated' | 'invoiceDate' = 'orderCreated'
    ): Observable<Blob> {
        const url = `${environment.apiUrl}excel-export/orders?company=${company}&division=${division}&fromDate=${fromDate}&toDate=${toDate}&dateType=${dateType}`;
        return this.http.get(url, { responseType: 'blob' });
    }

    // ✅ Export order headers + lines
    exportOrderHeadersWithLines(
        company: string,
        division: string,
        fromDate: string,
        toDate: string,
        dateType: 'orderCreated' | 'invoiceDate' = 'orderCreated'
    ): Observable<Blob> {
        const url = `${environment.apiUrl}orders-export/HeaderAndLines?fromDate=${fromDate}&toDate=${toDate}&company=${company}&division=${division}&dateType=${dateType}`;
        return this.http.get(url, { responseType: 'blob' });
    }
 
    getOrderHeaders(pageNumber: number, pageSize: number = 100, sortBy: string = 'DESC', orderType: string = 'CustomerOrder') {
        const url = `${environment.apiUrl}Order/GetOrderHeaders`;
        return this.http.get<any>(url, {
            params: this.appendOrderListWarehouseParam({
                pageNumber,
                pageSize,
                sortBy,
                orderType,
                company: this.getCompanyId(),
                division: this.getDivisionId()
            })
        });
    }
 
    globalSearchOrderHeaders(searchTerm: string, pageNumber: number = 0, pageSize: number = 10000, sortBy: string = 'DESC', orderType: string = 'CustomerOrder'): Observable<any> {
        const url = `${this.baseUrl}Order/GlobalSearchOrderHeaders`;
        return this.http.get<any>(url, {
            params: this.appendOrderListWarehouseParam({
                searchTerm,
                pageNumber,
                pageSize,
                sortBy,
                orderType,
                company: this.getCompanyId(),
                division: this.getDivisionId()
            })
        });
    }

    /**
     * Resolve Torna orders for an M3 delivery number (DLIX).
     * Enabled for divisions in environment.deliverySearchEnabledDivisions.
     * Backend: MWS411MI/LstDeliveryLine → RIDN → Torna referenceOrderId.
     */
    findOrdersByDeliveryNumber(dlix: string): Observable<any> {
        const url = `${this.baseUrl}m3/orders-by-delivery`;
        return this.http.get<any>(url, {
            params: this.appendOrderListWarehouseParam({
                dlix: String(dlix ?? '').trim(),
                company: this.getCompanyId(),
                division: this.getDivisionId()
            })
        });
    }

    getOrderHeadersByUserResponsible(
        userIdResponsible: string,
        pageNumber: number = 0,
        pageSize: number = 10000,
        sortBy: string = 'DESC',
        orderType: string = 'CustomerOrder'
    ): Observable<any> {
        const url = `${this.baseUrl}Order/GetOrderHeaders/UserResponsible`;
        return this.http.get<any>(url, {
            params: this.appendOrderListWarehouseParam({
                userIdResponsible,
                pageNumber,
                pageSize,
                sortBy,
                orderType,
                company: this.getCompanyId(),
                division: this.getDivisionId()
            })
        });
    }
 
    getHeaderInfo(): Observable<any> {
        const url = `${environment.apiUrl}HeaderInfo`;
        return this.http.get<any>(url, {
            params: {
                company: this.getCompanyId(),
                division: this.getDivisionId()
            }
        });
    }
 
    // getItemDetails(itemForm: { itemNumber: string; cuno: string; orno: string; whlo: string }) {
    //     const url = `${environment.apiUrl}m3/item/itemdetails`;
    //     return this.http.post<any>(url, itemForm, {
    //         params: {
    //             company: this.getCompanyId(),
    //             division: this.getDivisionId()
    //         }
    //     });
    // }
 
    /**
     * Update order header status.
     * Calls: /Order/UpdateOrderHeader/StatusUpdate?orderType=CustomerOrder&company=...&division=...&orderNumber=...&orderStatus=...
     * Accepts company and division as optional parameters for flexibility (e.g., from selected row).
     */
    updateOrderHeaderStatus(orderNumber: string, orderStatus: number, orderType: string, company?: string, division?: string): Observable<any> {
        const url = `${this.baseUrl}Order/UpdateOrderHeader/StatusUpdate`;
        return this.http.put<any>(
            url,
            {},
            {
                params: {
                    orderType,
                    company: company || this.getCompanyId(),
                    division: division || this.getDivisionId(),
                    orderNumber,
                    orderStatus
                }
            }
        );
    }
 
    searchItemDescription(itemNumber: string) {
        const url = `${environment.apiUrl}/m3/item/search`;
        return this.http.get<any>(url, {
            params: {
                SQRY: `FUDS:${encodeURIComponent(itemNumber)}`,
                company: this.getCompanyId(),
                division: this.getDivisionId()
            }
        });
    }
 
    getOrderLines(orderId: string) {
        const url = `${this.baseUrl}/Order/GetOrderLine`;
        return this.http.get(url, {
            params: {
                orderId: orderId,
                company: this.getCompanyId(),
                division: this.getDivisionId()
            }
        });
    }
 
    getOrderHeader(orderNumber: string, orderType: string = 'CustomerOrder') {
        const url = `${this.baseUrl}Order/GetOrderHeader`;
        return this.http.get<any>(url, {
            params: {
                orderNumber,
                company: this.getCompanyId(),
                division: this.getDivisionId(),
                orderType
            }
        });
    }
 
    confirmOrder(body: any, orderType: string = 'CustomerOrder') {
        const url = `${this.baseUrl}Order/BatchOrder/Confirm`;
        return this.http.post<any>(url, body, {
            params: {
                orderType,
                company: this.getCompanyId(),
                division: this.getDivisionId()
            }
        });
    }

    fetchFirmOrderReleaseDetailsFromM3(payload: { status: any; referenceOrderId: any; company?: string; division?: string }): Observable<any> {
        const url = `${this.baseUrl}Order/FetchFirmOrderReleaseDetailsFromM3`;
        console.log('Payload being sent:', payload);
        console.log('Full URL:', `${url}?status=${payload.status}&referenceOrderId=${payload.referenceOrderId}&company=${payload.company || this.getCompanyId()}&division=${payload.division || this.getDivisionId()}`);
        return this.http.post<any>(url, payload, {
            params: {
                status: payload.status ?? '',
                referenceOrderId: String(payload.referenceOrderId ?? ''),
                company: payload.company || this.getCompanyId(),
                division: payload.division || this.getDivisionId()
            }
        });
    }

    cancelOrder(orderId: string, cancellationReason: string, orderType: string = 'CustomerOrder'): Observable<any> {
    const url = `${this.baseUrl}Order/api/orders/${orderId}/cancel`;
    const cancelledBy = this.inMemoryStorageService.get('userName') || '';
    const company = this.getCompanyId();
    const division = this.getDivisionId();
    
    return this.http.post<any>(
        url,
        { cancellationReason },
        {
            params: {
                orderType,
                cancelledBy,
                company,
                division
            }
        }
    );
}

    /** Persist credit-limit Sync/Invoice approval (reason + audit on order header). */
    approveCreditLimit(
        orderNumber: string,
        reason: string,
        orderType: string = 'CustomerOrder'
    ): Observable<any> {
        const url = `${this.baseUrl}Order/api/orders/${encodeURIComponent(orderNumber)}/credit-limit-approve`;
        const approvedBy =
            this.inMemoryStorageService.get('email') ||
            this.inMemoryStorageService.get('userName') ||
            '';
        const company = this.getCompanyId();
        const division = this.getDivisionId();
        return this.http.post<any>(
            url,
            { reason },
            {
                params: {
                    orderType,
                    approvedBy: String(approvedBy),
                    company,
                    division
                }
            }
        );
    }

    /** Persist margin approval (reason + audit on order header). */
    approveMargin(
        orderNumber: string,
        reason: string,
        orderType: string = 'CustomerOrder'
    ): Observable<any> {
        const url = `${this.baseUrl}Order/api/orders/${encodeURIComponent(orderNumber)}/margin-approve`;
        const approvedBy =
            this.inMemoryStorageService.get('email') ||
            this.inMemoryStorageService.get('userName') ||
            '';
        const company = this.getCompanyId();
        const division = this.getDivisionId();
        return this.http.post<any>(
            url,
            { reason },
            {
                params: {
                    orderType,
                    approvedBy: String(approvedBy),
                    company,
                    division
                }
            }
        );
    }

    deleteStatus15BatchOrder(orderNumber: string, orderType: string = 'CustomerOrder'): Observable<any> {
        const url = `${this.baseUrl}Order/api/orders/${orderNumber}/delete-status15-batch`;
        const cancelledBy = this.inMemoryStorageService.get('userName') || '';
        const company = this.getCompanyId();
        const division = this.getDivisionId();

        return this.http.post<any>(url, null, {
            params: {
                orderType,
                cancelledBy,
                company,
                division
            }
        });
    }

    checkStatus15Ois300(orderNumber: string, orderType: string = 'CustomerOrder'): Observable<any> {
        const url = `${this.baseUrl}Order/api/orders/${orderNumber}/check-status15-ois300`;
        const company = this.getCompanyId();
        const division = this.getDivisionId();

        return this.http.get<any>(url, {
            params: {
                orderType,
                company,
                division
            }
        });
    }
    /**
     * Sync Orders to M3 for the current company/division.
     * Calls: /api/Order/SyncToM3?orderType=CustomerOrder&company=...&division=...
     */
    syncOrdersToM3(orderType: string = 'CustomerOrder'): Observable<any> {
 
        const url = `${this.baseUrl}Order/SyncToM3`;
              const syncUserEmail = this.inMemoryStorageService.get('email') || '';
        const tornaUserId = this.inMemoryStorageService.get('userName') || '';
        return this.http.post<any>(
            url,
            {}, // No body required
            {
                params: {
                    orderType,
                    company: this.getCompanyId(),
                    division: this.getDivisionId(),
                    syncUserEmail,
                    tornaUserId
                }
            }
        );
    }
    /**
 * Sync single order to M3.
 * Calls: /api/Order/SyncToM3?orderNumber=...&orderType=CustomerOrder&company=...&division=...
 */
syncSingleOrderToM3(orderNumber: string, orderType: string = 'CustomerOrder'): Observable<any> {
    const url = `${this.baseUrl}Order/SyncToM3`;
    const syncUserEmail = this.inMemoryStorageService.get('email') || '';
    const tornaUserId = this.inMemoryStorageService.get('userName') || '';
    return this.http.post<any>(
        url,
        {}, // No body required
        {
            params: {
                orderNumber,
                orderType,
                company: this.getCompanyId(),
                division: this.getDivisionId(),
                syncUserEmail,
                tornaUserId
            }
        }
    );
}

    /**
     * Pre-sync customer block check via CRS610MI/GetFinancial (BLCD).
     * Calls: /api/financial/customer-block-check?cuno=...&cono=...&divi=...
     */
    checkCustomerBlock(cuno: string, cono?: string, divi?: string): Observable<{
        allowed: boolean;
        blcd?: string;
        cuno?: string;
        cunm?: string;
        message?: string;
    }> {
        const url = `${this.baseUrl}financial/customer-block-check`;
        return this.http.get<any>(url, {
            params: {
                cuno: cuno ?? '',
                cono: cono || this.getCompanyId(),
                divi: divi || this.getDivisionId()
            }
        });
    }

    /**
     * Salesperson over CL3/CL4: set M3 BLCD=2 via CRS610MI/ChgFinancial.
     * Used after over-limit Save as Draft (header + bulk lines). Sync path does not call this.
     * Calls: POST /financial/set-customer-credit-block?cuno=&cono=&divi=
     */
    setCustomerCreditBlock(cuno: string, cono?: string, divi?: string): Observable<{
        applied?: boolean;
        skipped?: boolean;
        success?: boolean;
        blcd?: string;
        cuno?: string;
        message?: string;
    }> {
        const url = `${this.baseUrl}financial/set-customer-credit-block`;
        return this.http.post<any>(
            url,
            {},
            {
                params: {
                    cuno: cuno ?? '',
                    cono: cono || this.getCompanyId(),
                    divi: divi || this.getDivisionId()
                },
                headers: getAuthHttpHeaders()
            }
        );
    }

    /**
     * Over CL3/CL4 on draft save: write BLCD=2 for all users, then complete.
     * Always completes so navigation can continue.
     */
    applyCustomerCreditBlockOnDraftSave(params: {
        cuno: string;
        cono?: string;
        divi?: string;
        divisionId: string;
        referenceOrderId?: unknown;
        creditApprovedSource?: { creditLimitApproved?: unknown; referenceOrderId?: unknown } | null;
    }): Observable<void> {
        if (!shouldSetCustomerCreditBlockOnDraftSave(params)) {
            return of(undefined);
        }
        const cuno = String(params.cuno ?? '').trim();
        if (!cuno) {
            return of(undefined);
        }
        return this.setCustomerCreditBlock(cuno, params.cono, params.divi || params.divisionId).pipe(
            catchError((err) => {
                console.warn('[CreditLimit] setCustomerCreditBlock failed', err);
                return of(null);
            }),
            map(() => undefined)
        );
    }

    /** @deprecated Use applyCustomerCreditBlockOnDraftSave for save path. */
    applyCustomerCreditBlockOnSyncHardBlock(params: {
        cuno: string;
        cono?: string;
        divi?: string;
        divisionId: string;
        userEmail?: string | null;
        referenceOrderId?: unknown;
        creditApprovedSource?: { creditLimitApproved?: unknown; referenceOrderId?: unknown } | null;
    }): Observable<void> {
        return this.applyCustomerCreditBlockOnDraftSave(params);
    }

        isSyncing: boolean = false;
    setSyncing(value: boolean): void {
        this.isSyncing = value;
        localStorage.setItem('isSyncing', value.toString());
    }
 
    getSyncing(): boolean {
        return this.isSyncing;
    }
    acquireOrderLock(orderNumber: string, companyId: string, divisionId: string, currentUsername: string): Observable<any> {
        const url = `${this.baseUrl}orders/lock/acquire?orderNumber=${orderNumber}&companyId=${companyId}&divisionId=${divisionId}&currentUsername=${currentUsername}`;
 
        // const url = `http://192.168.31.92:8444/orders/lock/acquire?orderNumber=${orderNumber}&companyId=${companyId}…
        return this.http.post(url, {});
    }
        searchCustomersLocalException(query: string): Observable<any[]> {
        const company = this.getCompanyId();
        const division = this.getDivisionId();
        // Use current division (100 or 400 from env)
        const url = `${environment.apiUrl}m3/SearchCustomer/SearchLocalException?SQRY=CUNM:${encodeURIComponent(query + '*')} STAT:${encodeURIComponent('20')}&company=${encodeURIComponent(company)}&division=${encodeURIComponent(division)}`;
        return this.http.get<any[]>(url);
    }
        searchCustomersNumberLocalException(query: string): Observable<any[]> {
        const company = this.getCompanyId();
        const division = this.getDivisionId();
        // Use current division (100 or 400 from env)
        const url = `${environment.apiUrl}m3/SearchCustomer/SearchLocalException?SQRY=CUNO:${encodeURIComponent(query + '*')} STAT:${encodeURIComponent('20')}&company=${encodeURIComponent(company)}&division=${encodeURIComponent(division)}`;
        return this.http.get<any[]>(url);
    }
}
 
export interface Product {
    orderNumber: any;
    status: number;
    addressId: string;
    id?: string;
    code?: string;
    name?: string;
    customerName?: string;
    description?: string;
    price?: number;
    quantity?: number;
    inventoryStatus?: string;
    category?: string;
    image?: string;
    rating?: number;
    // New order fields
    customer?: string;
    address?: string;
    orderTypeId?: string;
    referenceOrderId?: string;
    facilityId?: string;
    warehouseId?: string;
    requestedDate?: string | number;
    outstanding?: string | number;
    totalOverdue?: string | number;
    okcrl3?: string | number;
    /** Credit Limit 4 threshold days from CRS610MI/GetFinancial (ODUD). */
    odud?: string | number;
    /** Number of overdue from MDBREADMI/GetCCUCRL00CC (NOOD). */
    nood?: string | number;
    creditLimit3?: string | number;
    phoneNumber?: string;
    countryCode?: string;
    vatRegistrationNo?: string;
    customerAddress?: string;
    /** 1 = custom delivery address only; 0 or omit = use master address list */
    isCustom?: number;
    /** Pipe-separated address for M3 AddBatchAddress (line1|line2|…); used when isCustom === 1 */
    customAddress?: string;
    defaultPriceList?: string;
    pltb?: string;
    cucd?: string;
    cfc4?: string;
    txap?: string;
    orderMovingStatus?: string;
    shouldFetchVatPercentage?: boolean;
    addressCountryCode?: string;
    cancellationReason?: string;
    cancelledBy?: string;
    creditLimitApproved?: boolean | number | string;
    creditLimitApprovedBy?: string;
    creditLimitApprovedAt?: string;
    creditLimitApprovalReason?: string;
    marginApproved?: boolean | number | string;
    marginApprovedBy?: string;
    marginApprovedAt?: string;
    marginApprovalReason?: string;
    m3LowestStatus?: string;
    m3HighestStatus?: string;
    invoiceDate?: string;
    invoiceDateObj?: Date | null;
    netAmount?: string | number;
    orderValue?: string | number;
    tornaOrderDate?: string;
    tornaOrderDateObj?: Date | null;
    orderCreatedDate?: string;
    userIdResponsible?: string;
    email?: string;
    mail?: string;
    /** Minimum order contribution margin (%) from SearchCustomer/Details F1N096. */
    minimumOrderMargin?: number;
    /** Persisted order margin snapshot from GetOrderHeader. */
    orderMcm?: number;
    orderCost?: number;
    /** Client-side computed flag from line/order margin checks. */
    marginBelowMinimum?: boolean;
    /** Client-side flag: order header contribution margin below minimum. */
    marginOrderBelowMinimum?: boolean;
    /** Client-side count of lines below minimum margin. */
    marginViolationLineCount?: number;
    F1N096?: string | number;
    errorMessage?: string;
    errorMessages?: string;
}
